import { Injectable, NgZone } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { contentSlug } from '../../../../content-slug';
import { CmsPage, PageTemplate, Region, SUBREGION_KIND, Suburb } from '../../../../models/content';
import { AccountService } from '../../../../services/account.service';
import { ModalService } from '../../../../services/modal.service';
import { RegionPageAction } from '../../content-region.component';
import {
  accountSnapshot,
  contentList,
  markRegionSuburbsChanged,
  nextIndex,
  regionDesignedPage,
  removeCached,
  shortId,
  suburbDesignedPage,
  upsertCached,
} from '../content/suburb-content';
import { displayName } from '../map/suburb-map.engine';
import { storedTemplate, TemplateKey } from '../template/suburb-template';
import { apiErrorBody } from './api-errors';
import { inZone } from './in-zone';
import { WorkspaceStateService } from './workspace-state.service';

type ContentKind = 'suburbs' | 'regions' | 'pages' | 'pageTemplates';

/** The confirm dialog before a record and its page are deleted. */
export interface RemoveAsk {
  title: string;
  copy: string;
  /** The confirm button's label ("Delete", "Remove"). */
  label: string;
}

/**
 * The website content records the Suburbs tool reads and writes (#256, #260): suburb, sub-region and place records,
 * regions, and their designed pages, in the account's content (the gated /account/content route). Reads come from the
 * cached account; every write goes to the server first and is then put in the cache, so the panels show it at once.
 */
@Injectable()
export class ContentRecordsService {
  constructor(
    private accountService: AccountService,
    private modalService: ModalService,
    private zone: NgZone,
    private state: WorkspaceStateService
  ) {}

  // ------------------------------------------------------------------ reads (the cached account)

  suburbs(): Suburb[] {
    return contentList<Suburb>(accountSnapshot(this.accountService)?.data?.content?.suburbs);
  }

  regions(): Region[] {
    return contentList<Region>(accountSnapshot(this.accountService)?.data?.content?.regions);
  }

  /** A suburb's page record (its salCode), if it has one. */
  suburbRecord(code: string | null): Suburb | undefined {
    return code ? this.suburbs().find((s) => s.salCode === code) : undefined;
  }

  /** A suburb, sub-region or place record by its id. */
  recordById(id: string | null | undefined): Suburb | undefined {
    return id ? this.suburbs().find((s) => s._meta.id === id) : undefined;
  }

  /**
   * Records that aren't one ABS suburb and aren't a sub-region (places, and any without a salCode or a kind): they
   * have no place on the map. Sub-regions are listed under their region instead.
   */
  otherPages(): Suburb[] {
    return this.suburbs().filter((s) => !s.salCode && s.kind !== SUBREGION_KIND);
  }

  /** A suburb's, sub-region's or place's designed page: the CMS page at its funeral-services address. */
  designedPage(record: Suburb, regions: Region[] = this.regions()): CmsPage | undefined {
    return suburbDesignedPage(accountSnapshot(this.accountService), record, regions);
  }

  regionPage(region: Region): CmsPage | undefined {
    return regionDesignedPage(accountSnapshot(this.accountService), region);
  }

  /** The Suburb or Sub-region page template as stored (#262), or null before it has been created. */
  template(key: TemplateKey): PageTemplate | null {
    return storedTemplate(accountSnapshot(this.accountService)?.data?.content?.pageTemplates, key);
  }

  // ------------------------------------------------------------------ new records (saved only when their editor saves)

  /** A suburb on the map: name and slug from ABS, its region, kind suburb + salCode; unpublished. */
  newSuburb(code: string, regionId: string, absName: string): Suburb {
    const name = displayName(absName);
    return {
      _meta: this.nextMeta(),
      name,
      slug: contentSlug(name),
      regionId,
      kind: 'suburb',
      salCode: code,
      published: false,
    };
  }

  /** A sub-region of this region (a district record), unpublished, in the colour slot given. */
  newSubRegion(regionId: string, mapColour: number): Suburb {
    return {
      _meta: this.nextMeta(),
      name: '',
      slug: '',
      regionId,
      kind: SUBREGION_KIND,
      published: false,
      mapColour,
    };
  }

  /** A page that isn't one suburb and isn't a sub-region: a place, like a memorial park. */
  newPlace(): Suburb {
    return {
      _meta: this.nextMeta(),
      name: '',
      slug: '',
      regionId: this.regions()[0]?._meta?.id ?? '',
      kind: 'place',
      published: false,
    };
  }

  newRegion(): Region {
    return {
      _meta: { id: `id-region-${shortId()}`, index: nextIndex(this.regions()) },
      slug: '',
      name: '',
      state: 'NSW',
      published: true,
    };
  }

  private nextMeta(): Suburb['_meta'] {
    return { id: `id-suburb-${shortId()}`, index: nextIndex(this.suburbs()) };
  }

  // ------------------------------------------------------------------ writes

  /** A region, then its designed page (the region editor's save contract). */
  writeWithPage(region: Region, pageAction: RegionPageAction | undefined): Observable<unknown> {
    return this.accountService.patchContentItem('regions', region).pipe(
      take(1),
      switchMap(() => this.pageWrite(pageAction))
    );
  }

  private pageWrite(pageAction: RegionPageAction | undefined): Observable<unknown> {
    if (pageAction?.action === 'upsert' && pageAction.page)
      return this.accountService.patchContentItem('pages', pageAction.page).pipe(take(1));
    if (pageAction?.action === 'remove' && pageAction.id)
      return this.accountService.removeContentItem('pages', pageAction.id).pipe(take(1));
    return of(null);
  }

  /**
   * Change one record from a freshly read copy, then cache it. A content write replaces the whole record, so a stale
   * copy would undo someone else's edit.
   */
  writeFresh(
    record: Suburb,
    change: (fresh: Suburb) => Suburb,
    done: (next: Suburb) => void
  ): void {
    this.freshWrite(record, change).subscribe({
      next: inZone(this.zone, (next: Suburb) => {
        done(next);
        this.state.redraw();
      }),
      error: inZone(this.zone, (err: unknown) => this.modalService.error(apiErrorBody(err))),
    });
  }

  /** writeFresh's request: the change made to a freshly read copy, written, then cached. Emits the record written. */
  freshWrite(record: Suburb, change: (fresh: Suburb) => Suburb): Observable<Suburb> {
    return this.accountService.get(true).pipe(
      take(1),
      switchMap(() => {
        const fresh = this.recordById(record._meta.id);
        if (!fresh) throw new Error(`"${record.name}" is no longer there`);
        return this.written(change(fresh));
      })
    );
  }

  /** A record that isn't stored yet, written and cached. Emits it. */
  create(record: Suburb): Observable<Suburb> {
    return this.written(record);
  }

  private written(record: Suburb): Observable<Suburb> {
    return this.accountService.patchContentItem('suburbs', record).pipe(
      take(1),
      map(() => {
        upsertCached(this.accountService, 'suburbs', record);
        return record;
      })
    );
  }

  /** Delete a record and (when given) its designed page, once the person confirms; then uncache both. */
  removeWithPage(
    record: Suburb,
    page: CmsPage | undefined,
    ask: RemoveAsk,
    done: () => void
  ): void {
    const modal = this.modalService.generic({
      title: ask.title,
      copy: [ask.copy],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        { label: ask.label, key: 'confirm', class: 'p-button-danger', icon: 'pi pi-trash' },
      ],
    });
    modal?.onClose.pipe(take(1)).subscribe((reason: string) => {
      if (String(reason).toLowerCase() !== 'confirm') return;
      const pageRemoval = page
        ? this.accountService.removeContentItem('pages', page._meta.id).pipe(take(1))
        : of(null);
      this.accountService
        .removeContentItem('suburbs', record._meta.id)
        .pipe(
          take(1),
          switchMap(() => pageRemoval)
        )
        .subscribe({
          next: inZone(this.zone, () => {
            removeCached(this.accountService, 'suburbs', record._meta.id);
            if (page) removeCached(this.accountService, 'pages', page._meta.id);
            done();
          }),
          error: inZone(this.zone, (err: unknown) => this.modalService.error(apiErrorBody(err))),
        });
    });
  }

  /** Put a saved record into the cached account (the server already has it). */
  cache(kind: ContentKind, item: Suburb | Region | CmsPage | PageTemplate): void {
    upsertCached(this.accountService, kind, item);
  }

  uncache(kind: ContentKind, id: string): void {
    removeCached(this.accountService, kind, id);
  }

  /** Flag a region as having suburb changes its next publish ships (best-effort; see suburb-content.ts). */
  markRegionChanged(regionId: string): void {
    markRegionSuburbsChanged(this.accountService, this.modalService, regionId);
  }

  showError(err: unknown): void {
    this.modalService.error(apiErrorBody(err));
  }
}
